export const getFirstQueryValue = (value: unknown): string | null => {
  if (!value) return null
  if (Array.isArray(value)) {
    const first = value.find((item): item is string => typeof item === 'string' && !!item)
    return first ?? null
  }
  return typeof value === 'string' && value ? value : null
}

/** Contract §1: `page` and `per_page` are integers ≥ 1, and `per_page` is capped at 500. */
const MIN_PAGINATION_VALUE = 1
export const MAX_PER_PAGE = 500

/**
 * A pagination query value as the API will accept it, or the store's value.
 *
 * A query string is arbitrary user input — a deep link or a hand-typed `?page=abc` would otherwise be
 * forwarded verbatim and answered with a `422` (§1). Anything that is not a whole number ≥ 1 falls
 * back to the store; an out-of-range `per_page` is clamped rather than discarded, since the intent is
 * clear. The result is never `0`, because `pick` drops falsy values and the param would vanish.
 */
export const toPaginationValue = (value: unknown, fallback: number, max: number = Number.MAX_SAFE_INTEGER): number => {
  const raw = getFirstQueryValue(value)
  const parsed = Number(raw)
  const candidate = raw !== null && Number.isInteger(parsed) && parsed >= MIN_PAGINATION_VALUE
    ? Math.min(parsed, max)
    : fallback

  return Number.isInteger(candidate) && candidate >= MIN_PAGINATION_VALUE
    ? Math.min(candidate, max)
    : MIN_PAGINATION_VALUE
}

const toWholeNumberAtLeast = (value: unknown, min: number): number | null => {
  const raw = getFirstQueryValue(value)
  const parsed = Number(raw)

  return raw !== null && Number.isInteger(parsed) && parsed >= min ? parsed : null
}

/** A database id from the query string, or `null` for anything that is not a whole number ≥ 1. */
export const toIdValue = (value: unknown): number | null => toWholeNumberAtLeast(value, 1)

/** A count from the query string (`0` included), or `null` for anything that is not a whole number ≥ 0. */
export const toCountValue = (value: unknown): number | null => toWholeNumberAtLeast(value, 0)

export const normalizeArrayQuery = (value: unknown): string[] | null => {
  if (!value) return null
  if (Array.isArray(value)) {
    const filtered = value.filter((item): item is string => typeof item === 'string' && !!item)
    return filtered.length ? filtered : null
  }
  return typeof value === 'string' && value ? [value] : null
}
